/**
 * Tenant warehouse Users list (`GET /admin/users`).
 *
 * Composition rule: display `membershipStatus`, `renewalDueSoon`, and `statusIsActive`
 * as sent. The "WStudio" chip is the MEMBERSHIP flag (`statusIsActive`, moves with purchases),
 * never the account flag (`isActive`, which is not membership). Do not AND dates to decide
 * ACTIVE. Do not treat empty `dimensionFamilies` as `NEVER_PURCHASED_MEMBERSHIP`. Chips are
 * formatting.
 */
import { useMemo, useState } from 'react';
import { Chip, Link, MenuItem, Stack, TextField, type ChipProps } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  listUsers,
  type MembershipStatusFilter,
  type UsersOrder,
  type UsersSort,
} from '@/api/warehouse/warehouseApi';
import type { WarehouseUser } from '@/api/warehouse/types';
import { FamilyChip } from '@/components/FamilyChip';
import { RosterTable, type RosterColumn } from '@/components/RosterTable';
import { EM_DASH, textOrDash } from '@/components/rosterFormat';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';

const PAGE_SIZE_DEFAULT = 20;

const MEMBERSHIP_OPTIONS: MembershipStatusFilter[] = [
  '',
  'ACTIVE',
  'GRACE',
  'LOST',
  'NEVER_PURCHASED_MEMBERSHIP',
];

function membershipChipColor(status: string): ChipProps['color'] {
  if (status === 'ACTIVE') return 'success';
  if (status === 'GRACE') return 'warning';
  return 'default';
}

function MembershipCell({
  value,
  t,
}: {
  value: string | null;
  t: (key: string, fallback?: string) => string;
}) {
  if (value === null) return EM_DASH;
  return (
    <Chip
      size="small"
      color={membershipChipColor(value)}
      label={t(`membershipStatus.${value}`, value)}
    />
  );
}

export function UsersPage() {
  const { t } = useCopy();
  const title = t('page.users.title', 'Users');
  useDocumentTitle(title);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT);
  const [search, setSearch] = useState('');
  const [membershipStatus, setMembershipStatus] = useState<MembershipStatusFilter>('ACTIVE');
  const [sort, setSort] = useState<UsersSort>('name');
  const [order, setOrder] = useState<UsersOrder>('asc');

  const resource = useResource(
    () => listUsers({ page, size, search, membershipStatus, sort, order }),
    [page, size, search, membershipStatus, sort, order],
  );

  const columns = useMemo<RosterColumn<WarehouseUser>[]>(
    () => [
      {
        id: 'name',
        header: t('users.columns.name', 'Name'),
        sortId: 'name',
        nowrap: true,
        render: (row) => {
          const label = textOrDash(row.name === null || row.name.length === 0 ? null : row.name);
          return (
            <Link component={RouterLink} to={`/users/${row.sourceUserId}`}>
              {label}
            </Link>
          );
        },
      },
      {
        id: 'email',
        header: t('users.columns.email', 'Email'),
        sortId: 'email',
        render: (row) => textOrDash(row.email),
      },
      {
        id: 'membershipStatus',
        header: t('users.columns.membershipStatus', 'Membership'),
        sortId: 'membershipStatus',
        render: (row) => <MembershipCell value={row.membershipStatus} t={t} />,
      },
      {
        id: 'families',
        header: t('users.columns.families', 'Families'),
        render: (row) => {
          if (row.dimensionFamilies.length > 0) {
            return (
              <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {row.dimensionFamilies.map((m) => (
                  <FamilyChip
                    key={`${m.dimensionSlug}:${m.familySlug}`}
                    dimensionName={m.dimensionName}
                    familyName={m.familyName}
                    color={m.color}
                    size="small"
                  />
                ))}
              </Stack>
            );
          }
          return EM_DASH;
        },
      },
      {
        id: 'renewalDueSoon',
        header: t('users.columns.renewalDueSoon', 'Renewal'),
        render: (row) =>
          row.renewalDueSoon === true ? (
            <Chip size="small" color="warning" label={t('users.renewalDueSoon', 'Due soon')} />
          ) : (
            EM_DASH
          ),
      },
      {
        id: 'statusIsActive',
        header: t('users.columns.wstudioActive', 'WStudio'),
        render: (row) =>
          row.statusIsActive ? (
            <Chip size="small" color="success" label={t('users.wstudioActive', 'Active')} />
          ) : (
            <Chip size="small" color="default" label={t('users.wstudioInactive', 'Inactive')} />
          ),
      },
    ],
    [t],
  );

  return (
    <RosterTable
      title={title}
      columns={columns}
      rows={resource.data?.content ?? []}
      rowId={(row) => String(row.sourceUserId)}
      status={resource.status}
      error={resource.error}
      totalElements={resource.data?.totalElements ?? 0}
      page={page}
      size={size}
      sort={sort}
      order={order}
      searchMode="liveMin2"
      toolbarExtra={
        <TextField
          select
          size="small"
          label={t('users.filter.membership', 'Membership')}
          value={membershipStatus}
          onChange={(event) => {
            setMembershipStatus(event.target.value as MembershipStatusFilter);
            setPage(0);
          }}
          sx={{ minWidth: 200 }}
        >
          {MEMBERSHIP_OPTIONS.map((value) => (
            <MenuItem key={value || 'all'} value={value}>
              {value === ''
                ? t('users.filter.all', 'All')
                : t(`membershipStatus.${value}`, value)}
            </MenuItem>
          ))}
        </TextField>
      }
      onPageChange={setPage}
      onRowsPerPageChange={(next) => {
        setSize(next);
        setPage(0);
      }}
      onSortChange={(id, nextOrder) => {
        setSort(id as UsersSort);
        setOrder(nextOrder);
        setPage(0);
      }}
      onApply={(query) => {
        setSearch(query.search);
        setPage(0);
      }}
      onRetry={resource.refetch}
    />
  );
}
